import request from "./request";
import { getFromStorage, setToStorage } from "@/service/utils";

const cred = `?product=${process.env.VUE_APP_KEY}`;
const user = getFromStorage("user");
const isUserLogin = user && user.user_id;

export function getAccessToken(params) {
  return request.get(`https://graph.facebook.com/oauth/access_token`, { params });
}

// TODO move cred to request intercepters

export function getBookList() {
  return request.get(`/v1/card_list/country/us${cred}`);
}

export function getBookInfo(id) {
  return request.get(`/v1/detail/${id}${cred}`);
}

export function batchGetBookInfo(data) {
  return request.post(`/v1/batch/detail${cred}`, data);
}

export function getBookChapters(bookId) {
  if (isUserLogin) {
    return request.get(`/user/chapters/${bookId}${cred}`);
  }
  return request.get(`/v1/chapters/${bookId}${cred}`);
}

export function getBookContent(bookId, chapterId) {
  return request.get(`/content/${bookId}/${chapterId}${cred}`, {
    responseType: "blob"
  });
}

export function getShelfRecommend() {
  return request.get(`/v1/card/shelf/country/us${cred}`);
}

// add book to my shelf
export function saveBooksToLib(bookId) {
  saveBooksToLocalLib(bookId);
  if (isUserLogin) {
    return request.post("/user/book_shelf", {
      book_id: bookId
    });
  } else {
    return Promise.resolve({});
  }
}

// get books from my shelf
export function getBooksFromLib(params) {
  if (isUserLogin) {
    return request.get(`/user/book_shelf${cred}`, { params });
  } else {
    const localLibs = getLibBooksFromLocal();
    const ids = localLibs.map(i => i.book_id);
    return batchGetBookInfo({ book_ids: ids }).then(books => {
      // merged with local read progress
      return mergeLocalProgress(books);
    });
  }
}

// remove books from my shelf
export function removeBooksFromLib(ids = []) {
  removeBooksToLocalLib(ids);
  if (isUserLogin) {
    return request.post("/user/remove_book_shelf", {
      book_ids: ids
    });
  } else {
    return Promise.resolve({});
  }
}

// add/update book's read progress
export function updateReadProgress(data, extra /* for local uasge */) {
  updateLocalReadProgress(data, extra);
  if (isUserLogin) {
    return request.post("/user/book_progress", {
      ...data,
      read_seconds: 0,
      page_index: 1
    });
  } else {
    return Promise.resolve({});
  }
}

// get read progress of one book
export function getBookReadProgress(bookId) {
  if (isUserLogin) {
    return request.get(`/user/book_progress/${bookId}${cred}`);
  } else {
    const localLibs = getLocalReadProgress();
    const books = localLibs.filter(b => b.book_id === bookId);
    return Promise.resolve(books[0] || {});
  }
}

// get user's read history
export function getReadHistory() {
  if (isUserLogin) {
    return request.get(`/user/read_progress${cred}`);
  } else {
    const localLibs = getLocalReadProgress();
    const ids = localLibs.map(i => i.book_id);
    return batchGetBookInfo({ book_ids: ids }).then(books => {
      // merged with local read progress
      return mergeLocalProgress(books);
    });
  }
}

export function loginFB(data) {
  return request.post("/login/fb_v2", data);
}

export function refreshToken() {
  if (isUserLogin) {
    return request.post(`/login/refresh_token`, {
      refresh_token: user.refresh_token
    });
  } else {
    return Promise.resolve({});
  }
}

export function buyCoins(data) {
  return request.post("/paypal/create", data);
}

export function unlockChapter(data) {
  return request.post("/pay/buy_chapter/paypal", data);
}

export function getConsumeOrders() {
  return request.get(`/pay/order_list${cred}`);
}

export function getCoinsRecord() {
  return request.get(`/user/coins_records/action/buy_chapter${cred}`);
}

export function getGlobalConfig() {
  return request.get(`/v1/global_config${cred}`);
}

export function getUserInfo() {
  return request.get(`/user/user_info${cred}`);
}

/**
 * local object， to match the api response
 * [{
 *    book_id: xx,
 *    book_name: xx, // not used for now, just for checking
 *    chapter_id: xx,
 *    chapter_index: xx,
 *    chapter_count: xx
 * }]
 */
const LOCAL_LIB_KEY = "LOCAL_LIB";

function updateLocalLibStatus(ids, status) {
  const localHistory = getLocalReadProgress();
  for (let i = 0; i < ids.length; i++) {
    const bookId = ids[i];
    const index = localHistory.map(i => i.book_id).indexOf(bookId);
    if (index > -1) {
      localHistory[index].inLib = status;
    }
  }
  setToStorage(LOCAL_LIB_KEY, localHistory);
}

function mergeLocalProgress(books) {
  const localLibs = getLibBooksFromLocal();
  const ids = localLibs.map(i => i.book_id);
  return books.map(book => {
    const index = ids.indexOf(book.id);
    if (index > -1) {
      book.read_progress = {
        chapter_index: localLibs[index].chapter_index || 0
      };
    }
    return book;
  });
}

export function saveBooksToLocalLib(bookId) {
  updateLocalLibStatus([bookId], true);
}

export function removeBooksToLocalLib(ids) {
  updateLocalLibStatus(ids, false);
}

export function getLibBooksFromLocal() {
  const localHistory = getLocalReadProgress();
  return localHistory.filter(b => b.inLib).sort((a, b) => (b.last_update || 0) - (a.last_update || 0));
}

export function updateLocalReadProgress(data, extraInfo) {
  const localHistory = getLocalReadProgress();
  // prepare lasted data
  const { book_id, chapter_id } = data;
  const book_name = extraInfo.bookName;
  const chapters = extraInfo.chapters;
  const chapter_index = chapters.map(i => i.id).indexOf(chapter_id);
  const merged = {
    book_id,
    chapter_id,
    book_name,
    chapter_index,
    chapter_count: chapters.length,
    last_update: Date.now() // new added, for sorting
  };
  // check is new
  const bookIndex = localHistory.map(i => i.book_id).indexOf(book_id);
  if (bookIndex > -1) {
    // => already has it, just replace
    localHistory[bookIndex] = Object.assign({}, localHistory[bookIndex], merged);
  } else {
    localHistory.push(merged);
  }
  // set local data back
  setToStorage(LOCAL_LIB_KEY, localHistory);
}

export function getLocalReadProgress(bookId) {
  const all = getFromStorage(LOCAL_LIB_KEY) || [];
  if (bookId) {
    const matched = all.filter(b => b.book_id === bookId);
    return matched[0];
  }
  return all.sort((a, b) => (b.last_update || 0) - (a.last_update || 0));
}

export function syncReadProgress(data) {
  return request.post("/user/book_progress_batch", data);
}

export function syncShelf(data) {
  return request.post("/user/book_shelf_batch", data);
}

/**
 *  getAccessToken(code) {
      getAccessToken({
        client_id: 420729019050953,
        code,
        client_secret: "af643945799f653a3919af1bd8b11ee7",
        redirect_uri: "https://3c6cc9a271a8.ngrok.io/"
      }).then(res => {
        if (res.access_token) {
          localStorage.setItem("access_token", res.access_token);
          this.getUser(res.access_token);
        } else {
          // handle error
        }
      });
    },
  */
